import { Icon } from '@coast/schemas';
import { isNil } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBScrollView } from '@shared/components/scrollview';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import { MBMenuRow } from '@shared/scenes/cards/bulk/components/menuRow';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';

interface CombinedTagsMenuProps {
  components: MBAPI.WorkflowTemplate_components_TagComponent[];
  selectedComponentIds?: string[];
  onTagComponentSelect(component: MBAPI.WorkflowTemplate_components_TagComponent): void;
}

export function CombinedTagsMenu({
  components,
  selectedComponentIds,
  onTagComponentSelect,
}: CombinedTagsMenuProps) {
  const colors = useContext(ColorThemeContext);
  const [filterValue, setFilterValue] = useState<string>('');
  const [filteredComponents, setFilteredComponents] = useState<
    MBAPI.WorkflowTemplate_components_TagComponent[]
  >([]);

  useEffect(() => {
    setFilteredComponents(
      components.filter(c => c.label?.toLowerCase().includes(filterValue.toLowerCase().trim())),
    );
  }, [components, filterValue]);

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={3} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('builder.combinedTags.fieldOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingHorizontal={2}>
        <MBSearchBar
          value={filterValue}
          onChangeText={val => {
            setFilterValue(!isNil(val) ? val : '');
          }}
          placeholder={i18n(`common.search`)}
        />
      </View>
      <MBScrollView style={{ maxHeight: grid(30) }}>
        <View paddingHorizontal={3} paddingTop={2} paddingBottom={1.5}>
          <MBTextBody2>{i18n('builder.combinedTags.helpText')}</MBTextBody2>
        </View>
        {filteredComponents?.map(component => {
          return (
            <TagField
              key={component.id}
              component={component}
              isSelected={selectedComponentIds?.includes(component.id) || false}
              onSelect={() => onTagComponentSelect(component)}
            />
          );
        })}
      </MBScrollView>
    </View>
  );
}

interface TagFieldProps {
  component: MBAPI.WorkflowTemplate_components_TagComponent;
  isSelected: boolean;
  onSelect(): void;
}

function TagField({ component, isSelected, onSelect }: TagFieldProps) {
  const colors = useContext(ColorThemeContext);

  return (
    <MBMenuRow
      isHidingPressableIndicator
      onPress={onSelect}
      title={component.label}
      key={component.id}
      skipWrappingIcon
      icon={isSelected && <MBIconV2 name="check-filled" size={grid(3)} color={colors.accent} />}
      image={
        <MBIconV2 name={component.icon as Icon} color={colors.foregroundActive} size={grid(3)} />
      }
      imageStyle={{ marginRight: grid(1) }}
    />
  );
}
